import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import useStarfishAPI from '../hooks/useStarfishAPI';
import { urlParams } from 'utils/request';
import Calendar from '../common/Calendar';
import { isEmpty } from 'lodash';
import CalendarDrawer from './CalendarDrawer';
import ErrorScreen from "./ErrorScreen";
import LoadingScreen from "./LoadingScreen";
import PropTypes from "prop-types";
import useDashboardError from "./useDashboardError";
import { SingleDatePicker } from "react-dates";
import css from './CalendarContainer.scss';
import Icon from "SF/MaterialIcon";
import { useDebounce } from "react-use";
import { useRefreshContext } from "./RefreshProvider";
import { RequestHelp } from "utils/types";

const AVAILABLE_TYPES = [
  "COURSE",
  "APPOINTMENT",
  "GROUP_SESSION",
  "TO_DO",
  "REFERRAL",
  "FLAG",
  "SECTION_MEETING_TIME"
];

const URL = '../student/getAgendaSummary.html';

const CalendarContainer = (props) => {
  const [response, callAPI] = useStarfishAPI();
  const [calendarData, setCalendarData] = useState();
  const [isDrawerOpen, setOpen] = useState(false);
  const [drawerData, setData] = useState([]);
  const [drawerDate, setDrawerDate] = useState("");
  const [dateFocused, setDateFocused] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [debouncedDate, setDebouncedDate] = useState(moment());
  // Maintains when the focus needs to be manually set in the UI when a
  // to-do is closed from the calendar widget
  const [focusID, setFocusID] = useState(0);
  const { refreshID } = useRefreshContext();
  // Tracks if event selected is an All Day event
  const [isAllDayEvent, setAllDayEvent] = useState(false);
  const closeButtonRef = useRef();

  // The selected date is debounced, meaning that it will update once with the
  // latest value available in a 350ms interval window. This is necessary so we
  // don't ping the backend too much in case users click too many times in the
  // arrow buttons
  useDebounce(
    () => {
      setDebouncedDate(selectedDate);
    },
    350,
    [selectedDate]
  );

  useEffect(() => {
    const startDate = debouncedDate.clone().startOf("week").format("YYYY-MM-DD")
    const endDate = debouncedDate.clone().endOf("week").add(1, 'days').format("YYYY-MM-DD")
    callAPI(URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: urlParams({
        startDate: startDate + " 00:00",
        endDate: endDate + " 00:00"
      })
    })
  }, [debouncedDate, refreshID]);

  const getEndTime = (endDate, type) => {
    // If no end date available, then block is considered an all day event
    if ( !endDate ) return null;

    const time = endDate.substr(11);

    // If block is an assignment which ends either 00:00 or 23:59, then it is considered
    // an all day event
    if ( type === "ASSIGNMENT" && (time === "00:00" || time === "23:59")) return null;
   
    // In all other cases the block has a valid end time and then it is not 
    // an all day event
    return time;
  }

  const getStartTime = (startDate, endDate, type) => {
    // If block has valid start and end dates or if the end date isn't present,
    // go with the current start time
    if ( startDate !== endDate || getEndTime(endDate, type) === null ) {
      return startDate.substr(11);
    }

    // Here we want to consider that the start time is 1h before the due time.
    // If the due time is anywhere before 1am, we just consider the start time being
    // midnight
    const dueTime = endDate.substr(11);
    const startTime =  moment(dueTime, "HH:mm").subtract(1, "hour").format("HH:mm");

    return startTime.substr(0, 2) === "23" ? "00:00" : startTime;
  }

  useDashboardError(response, props.setError)

  const getInfo = ({type, ...item}) => {
    let [blockType, name, details] = ["", "", {}];

    switch (type) {
      case "COURSE":
        blockType = "ASSIGNMENT";
        name = item.assignment.name;
        details = {
          instructors: item.instructors,
          section: item.section,
          id: item.id
        };
        break;
      case "APPOINTMENT":
        blockType = "APPOINTMENT";
        name = item.reason;
        details = {
          instructor: item.instructor,
          location: item.location,
          reason: item.reason,
          id: item.id,
          section: item.section,
        };
        break;
      case "GROUP_SESSION":
        blockType = "GROUP_SESSION";
        name = item.title;
        details = {
          instructor: item.instructor,
          location: item.location,
          reason: item.reason,
          id: item.id,
          section: item.section,
        };
        break;
      case "TO_DO":
        blockType = "TO_DO";
        name = item.name;
        details = {
          id: item.id,
          section: item.section,
          raiser: item.raiser,
          assignee: item.assignee,
          raiseNotes: item.raiseNotes,
          raisedDate: item.raisedDate,
          studentCloseable: item.studentCloseable,
          closureReviewRequired: item.closureReviewRequired,
          requestedResolutionDate: item.requestedResolutionDate
        };
        break;
      case "REFERRAL":
        blockType = "REFERRAL";
        name = item.name;
        details = {
          id: item.id,
          section: item.section,
          raiser: item.raiser,
          assignee: item.assignee,
          raiseNotes: item.raiseNotes,
          raisedDate: item.raisedDate
        };
        break;
      case "FLAG":
        blockType = "FLAG";
        name = item.name;
        details = {
          id: item.id,
          section: item.section,
          raiser: item.raiser,
          assignee: item.assignee,
          raiseNotes: item.raiseNotes,
          raisedDate: item.raisedDate
        };
        break;
      case "SECTION_MEETING_TIME":
        blockType = "CLASS";
        name = item.title;
        details = {
          id: item.id,
          title: item.title,
          services: item.services,
          instructors: item.instructors,
          schedules: item.schedules,
          sectionId: item.sectionId
        };
        break;

    }
    return { type: blockType, name, details };
  }

  const getStartHour = allStartTimes => {
    const defaultStartHour = 6;
    const minOfAllStartTimes = Math.min(
      ...allStartTimes.map(startTime => moment(startTime, "HH:mm").hour())
    );
    return Math.min(defaultStartHour, minOfAllStartTimes);
  }

  const parseData = data => {
    if ( isEmpty(data.days) ) return { slots: [] };

    const slots = [];
    const allStartTimes = [];
     data.days.forEach(({ items, startDate }) => {
         items
           // Other types will come in next tickets
           .filter(item => AVAILABLE_TYPES.indexOf(item.type) > -1)
           .forEach(item => {
             const { type, name, details } = getInfo(item);
             const startTime = getStartTime(item.startDate, item.endDate, type);
             const endTime = getEndTime(item.endDate, type);

             // If event has an end time, meaning it isn't an all day event, so
             // we should evaluate its start time to establish which is the earliest
             // event of the period
             if (endTime) allStartTimes.push(startTime);

             slots.push({
               id: `${name}-${item.id}`,
               date: startDate.substr(0, 10),
               startTime,
               endTime,
               name,
               type,
               details,
               originalItem: item
             })
           });
       }
     );
    return { slots, startHour: getStartHour(allStartTimes) };
  }

  const onEventItemClick = (data, date, isAllDay) => {
    setOpen(true);
    setDrawerDate(date);
    setData(data);
    setAllDayEvent(isAllDay);
  };

  const drawerContains = calendarItem => {
    return drawerData.some(
        drawerItem => drawerItem.id === calendarItem.id && drawerItem.type === calendarItem.type
    );
  }

  const calendarContainsNewPendingReviewItem = ({originalItem}) => {
    return response.data.days.some(
        calendarDay =>
            calendarDay.items.some(
                item => item.id === originalItem.id && item.type === originalItem.type && item.requestedResolutionDate !== originalItem.requestedResolutionDate
            )
    );
  }

  // When the student takes action on a to-do, [response] will have the updated active calendar items.
  // Update the data in the calendar drawer.
  useEffect(() => {
    if (!isDrawerOpen) return;

    const hasNewPendingReviewItem = drawerData.some(drawerItem => calendarContainsNewPendingReviewItem(drawerItem));
    const { slots: calendarItems } = parseData(response.data);
    const newDrawerData = calendarItems.filter(calendarItem => calendarItem.date === drawerDate && drawerContains(calendarItem));

    if (newDrawerData.length < drawerData.length || hasNewPendingReviewItem) {
      setData(newDrawerData);
      // For screenreaders, after a student takes action on a to-do from
      // the calendar drawer, return focus to calendar drawer close button.
      closeButtonRef.current && closeButtonRef.current.focus();
    }
  }, [response, drawerData, isDrawerOpen, drawerDate]);

  useEffect(() => {
    if(!response.pending && !isEmpty(response.data))
      setCalendarData(response.data);
  }, [response]);

  const getCalendar = () => {
    if ( isEmpty(calendarData) ) return null;
    const { slots, startHour } = parseData(calendarData);
    return (
      <Calendar
        data={slots}
        startHour={startHour}
        onEventClick={onEventItemClick}
        onHeaderClick={onEventItemClick}
        selectedDate={debouncedDate.format("YYYY-MM-DD")}
        focusID={focusID}
      />
    );
  }

  // This is necessary so we can add a label to the calendar button, once react-dates
  // isn't allowing the label to be overridden without having to explicitly set all
  // default labels
  useEffect(() => {
    document
      .querySelector("#calendar-date-select").parentNode.parentNode.querySelector("button")
      .setAttribute('aria-label', 'Open Calendar');
  }, []);

  return (
    <>
      <div className={css['date']}>
        <label htmlFor="calendar-date-select">Choose Date:</label>
        <div>
          <SingleDatePicker
            date={selectedDate}
            onDateChange={date => {
              setSelectedDate(date);

              // Make sure that after the date is selected we don't lose focus
              document.querySelector("#calendar-date-select").focus();
            }}
            focused={dateFocused}
            onFocusChange={({ focused }) => setDateFocused(focused)}
            id="calendar-date-select"
            numberOfMonths={1}
            displayFormat="MMMM D, YYYY"
            placeholder={moment().format("MMMM D, YYYY")}
            showDefaultInputIcon
            inputIconPosition="after"
            noBorder
            hideKeyboardShortcutsPanel
            isOutsideRange={() => false}
            readOnly
          />
          <button 
            aria-label="Go to previous week"
            title="Go to previous week"
            data-role="previousCalendarWeek"
            onClick={() => setSelectedDate(selectedDate.clone().subtract(1, "week"))}>
            <Icon icon="chevron_left" />
          </button>
          <button 
            aria-label="Go to this week"
            title="Go to this week"
            onClick={() => setSelectedDate(moment())}>
            <Icon icon="today" />
          </button>
          <button 
            aria-label="Go to next week" 
            title="Go to next week" 
            data-role="nextCalendarWeek"
            onClick={() => setSelectedDate(selectedDate.clone().add(1, "week"))}>
            <Icon icon="chevron_right" />
          </button>
        </div>
      </div>
      {response.pending && <LoadingScreen />}
      {response.error && <ErrorScreen />}
      {getCalendar()}
      <CalendarDrawer
        ref={closeButtonRef}
        isOpen={isDrawerOpen}
        onClose={() => {
          setFocusID(focusID + 1);
          setOpen(false);
        }}
        data={drawerData}
        date={drawerDate}
        successToastMessage={props.successToastMessage}
        requestHelp={props.requestHelp}
        isAllDayEvent={isAllDayEvent}
      />
    </>
  )
}

CalendarContainer.propTypes = {
  setError: PropTypes.func.isRequired,
  successToastMessage: PropTypes.func.isRequired,
  requestHelp: RequestHelp.isRequired
};

export default CalendarContainer;
